import React, { useEffect, useMemo, useState } from "react";
import { Bar, Line, Pie } from "react-chartjs-2";
import annotationPlugin from 'chartjs-plugin-annotation';
import {
  Chart as ChartJS,
  BarElement,
  LineElement,
  ArcElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  PointElement,
} from "chart.js";

ChartJS.register(
  annotationPlugin,
  BarElement,
  LineElement,
  ArcElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  PointElement
);

ChartJS.register({
	id: 'textInside',
	afterDatasetsDraw: function (chart, _) {
    //if (chartType === "pie") return;
    const options = chart.config.options.plugins?.textInside;
    if (!options) return;
    if (options.type !== "pie") return;
		const ctx = chart.ctx;
    ctx.save()
		const fontSize = options.fontSize;
		ctx.font = fontSize + 'px Arial';
		ctx.fillStyle = options.color;
		ctx.textAlign = 'center';
		ctx.textBaseline = 'middle';
		const ntext = options.text;
		const textX = (chart.chartArea.right - chart.chartArea.left) / 2; //Math.round(width / 2);
		const textY = (chart.chartArea.bottom - chart.chartArea.top) / 2; // + 100; //Math.round(height * 0.7);      
		ctx.fillText(ntext, textX, textY);
    ctx.font = '15px Arial';
    ctx.fillText("Hours", textX, textY + 25)
    ctx.restore()
	}
});

function computeDayTotals(records) {
  let dayTotals = { Monday: 0, Tuesday: 0, Wednesday: 0, Thursday: 0, Friday: 0, Saturday: 0, Sunday: 0, };
  records.forEach((record) => {
    try {
      const dateStr = record.TimesheetDate;
      if (!dateStr) return;

      const dateObj = new Date(dateStr);
      if (isNaN(dateObj)) {
        console.warn("Invalid date format:", dateStr);
        return;
      }

      const weekday = dateObj.toLocaleDateString("en-GB", { weekday: "long" });

      if (dayTotals.hasOwnProperty(weekday)) {
        dayTotals[weekday] += (Number(record.TimesheetHours) || 0);
      }
    } catch (err) {
      console.warn("Error processing record:", record, err);
    }
  });
  return dayTotals;
}

const TimeChart = ({ user, records }) => {
  const [chartType, setChartType] = useState(user?.UserCustomSettings?.preferredGraph || "line");
  const [text, setText] = useState("");
  let dayTotals = useMemo(() => computeDayTotals(records), [records])
  let data = {
    labels: Object.keys(dayTotals),
    datasets: []
  };
  let options = {
    responsive: true,
    plugins: {
      textInside: {
        text: () => "0",
				color: 'black',
				fontSize: 32
			}
    },
    scales: {
      y: {
        stacked: true,
        beginAtZero: true,
        ticks: { stepSize: 1 },
      },
      x: {
        stacked: true
      }
    }
  };

  data = {
    labels: Object.keys(dayTotals),
    datasets: [
      {
        label: "Hours Worked",
        data: Object.values(dayTotals),
        backgroundColor: "#007bff",
        borderColor: "#007bff",
        fill: chartType === "line" ? false : true,
      },
      {
        label: "Over Shift Hours",
        data: [],
        backgroundColor: 'rgb(207, 230, 235)', //'rgba(28, 134, 161, 0.21)'
        borderColor: 'rgb(207, 230, 235)'
      },
      {
        label: "Under Shift Hours",
        data: [],
        backgroundColor: 'rgb(235, 207, 221)', //'rgba(161, 28, 94, 0.21)'
        borderColor: 'rgb(235, 207, 221)'
      }
    ],
  };

  options = {
    responsive: true,
    height: 250,
    ...(chartType === "pie" ? { radius: 100 } : {}),
    maintainAspectRatio: false,
    cutout: '75%',
    plugins: {
      legend: {
        display: true,
        position: chartType === "pie" ? 'right' : 'top'
      },
      annotation: {
        annotations: {
          box1: {
            type: 'box',
            yMin: user?.UserDailyHours || 7.5,
            yMax: 13,
            backgroundColor: 'rgba(28, 134, 161, 0.21)',
            borderWidth: 0,
            drawTime: 'beforeDatasetsDraw'
          },
          /*label1: {
            type: "label",
            xValue: 3,
            yValue: (13 + (user?.dailyHours || 7.5)) / 2,
            backgroundColor: 'rgba(0,0,0,0)',
            content: ["Over Shift Hours"],
            font: {
              size: 24
            },
            color: 'rgba(61, 61, 61, 0.82)'
          },*/
          box2: {
            type: 'box',
            yMax: user?.UserDailyHours || 7.5,
            yMin: 0,
            backgroundColor: 'rgba(161, 28, 94, 0.21)',
            borderWidth: 0,
            drawTime: 'beforeDatasetsDraw'
          },
          /*label2: {
            type: "label",
            xValue: 3,
            yValue: (user?.dailyHours || 7.5) / 2,
            backgroundColor: 'rgba(255, 255, 255, 0)',
            content: ["Under Shift Hours"],
            font: {
              size: 24
            },
            color: 'rgba(61, 61, 61, 0.82)'
          }*/
        }
      },
      textInside: {
        text: text,
        type: chartType,
				color: 'black',
				fontSize: 32
			}
    },
    scales:
      chartType !== "pie"
        ? {
            y: {
              stacked: true,
              beginAtZero: true,
              ticks: { stepSize: 1 },
            },
            x: {
              stacked: true
            }
          }
        : {},
  };

  const pieData = {
    labels: Object.keys(dayTotals),
    datasets: [
      {
        label: "Hours Worked",
        data: Object.values(dayTotals),
        backgroundColor: [
          "#c7522a",       // Red
          "#d68a58",     // Orange
          "#e5c185",     // Yellow
          "#fbf2c4",       // Green
          "#74a892",       // Blue
          "#3a978c",      // Indigo
          "#008585"      // Violet
        ],
        borderColor: [
          "#c7522a",       // Red
          "#d68a58",     // Orange
          "#e5c185",     // Yellow
          "#fbf2c4",       // Green
          "#74a892",       // Blue
          "#3a978c",      // Indigo
          "#008585"      // Violet
        ],
        fill: chartType === "line" ? false : true,
      }
    ],
  }

  useEffect(() => {
    //console.log(`'${chartType}'`, chartType === "pie", dayTotals, text)
    setText(chartType === "pie" ? Object.values(dayTotals).reduce((sum, h) => sum + h, 0).toString() : "")
  }, [chartType, dayTotals])

  const renderChart = () => {
    switch (chartType) {
      case "line":
        return <div style={{ maxHeight: 300, maxWidth: 600, display: "flex", justifyContent: "center" }}><Line data={data} options={options}  width={600} height={300} /></div>;
      case "pie":
        return <div style={{ maxHeight: 300, maxWidth: 450, display: "flex", justifyContent: "center" }}><Pie data={pieData} options={options}  width={600} height={300} /></div>;
      default:
        return <div style={{ maxHeight: 300, maxWidth: 600, display: "flex", justifyContent: "center" }}><Bar data={data} options={options} width={600} height={300} /></div>;
    }
  };

  return (
    <div style={{ maxWidth: "600px", margin: "20px auto" }}>
      <h3>Time Recorded Per Day of the Week</h3>

      <select
        value={chartType}
        onChange={(e) => setChartType(e.target.value)}
        style={{ marginBottom: "10px", padding: "5px" }}
      >
        <option value="bar">Bar Chart</option>
        <option value="line">Line Chart</option>
        <option value="pie">Pie Chart</option>
      </select>

      {renderChart()}
    </div>
  );
};

export default TimeChart;
